import { IClassName } from '@frontend/jetlend-core/src/models/common';
import React, { PropsWithChildren } from 'react';
import {
    KeyframeOptions,
    Transition,
} from 'motion';
import {
    motion,
    AnimatePresence,
} from 'motion/react';

import { ANIMATION_VARIANTS } from './ContentTransitionWrapper.logic';

export type TransitionType = 'fade' | 'slide' | 'scale';

export interface IProps extends PropsWithChildren, IClassName, Partial<Pick<KeyframeOptions, 'ease'>> {
    /**
     * Ключ для определения необходимости анимации перехода.
     * При изменении этого ключа запускается анимация смены контента.
     * Ключ может быть только примитивом
     */
    transitionKey: boolean | number | string;
    /**
     * Тип анимации
     */
    transitionType?: TransitionType;
    /**
     * Длительность анимации в секундах
     */
    duration?: 0.2 | 0.4 | 0.8;
}

/**
 * Компонент-обёртка для анимации смены контента с использованием Framer Motion
 *
 * @description Универсальный компонент для плавной анимации переходов между
 * различными состояниями контента с использованием motion.dev.
 *
 * @example
 * ```tsx
 * <ContentTransitionWrapper
 *   transitionKey={condition}
 *   duration={0.4}
 *   transitionType="fade"
 *   ease="easeInOut"
 * >
 *   {condition ? <FirstComponent /> : <SecondComponent />}
 * </ContentTransitionWrapper>
 * ```
 *
 * @returns JSX элемент с анимированным содержимым
 */
export default function ContentTransitionWrapper({
    transitionKey,
    transitionType = 'fade',
    duration = 0.2,
    ease = 'easeInOut',
    className,
    children,
}: IProps) {
    // Если нет дочерних элементов - возвращаем null
    if (!React.Children.toArray(children).some(Boolean)) {
        return null;
    }

    const variants = ANIMATION_VARIANTS[transitionType] || ANIMATION_VARIANTS.fade;

    const transition: Transition = {
        duration,
        ease,
    };

    return (
        <AnimatePresence mode="wait" initial={false}>
            <motion.div
                key={typeof transitionKey === 'boolean' ? Number(transitionKey) : transitionKey}
                variants={variants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={transition}
                className={className}
            >
                {children}
            </motion.div>
        </AnimatePresence>
    );
}